/** Longest desktop label the strip shows (`toLocaleDateString` short month). */
export const HOTEL_SEARCH_DATE_SAMPLE = 'Sep 30, 2026';

export const HOTEL_SEARCH_DATE_FONT_SIZE_PX = 20;

/**
 * Glyph width of HOTEL_SEARCH_DATE_SAMPLE at 20px Host Grotesk bold.
 */
export const HOTEL_SEARCH_DATE_TEXT_MIN_WIDTH_PX = 130;

/** Side padding on `.hotel-search-field` at 900px and at 1200px in HotelForm.css. */
export const HOTEL_SEARCH_DATE_FIELD_PADDING_X_MD_PX = 14;
export const HOTEL_SEARCH_DATE_FIELD_PADDING_X_LG_PX = 20;

/** Packed destination column: long enough for the placeholder, never grows into the row. */
export const HOTEL_SEARCH_DESTINATION_WIDTH = '240px';
export const HOTEL_SEARCH_DESTINATION_MIN_WIDTH = '160px';

export const HOTEL_SEARCH_STRIP_COLUMN_GAP = '16px';
/** Space between the two rows when the bar wraps below the wide desktop layout. */
export const HOTEL_SEARCH_STRIP_ROW_GAP = '12px';

/**
 * Strip padding. Below the wide desktop row the bar is two lines, so it does
 * not reserve a corner for an absolutely positioned button.
 */
export function getHotelSearchStripPadding(isEmbedded) {
  if (isEmbedded) return 0;
  return {
    xs: '16px 20px',
    md: '12px 24px',
    lg: '12px 12px 12px 36px',
  };
}

export function getHotelSearchDateColumnMinWidth(paddingX = HOTEL_SEARCH_DATE_FIELD_PADDING_X_LG_PX) {
  return `${HOTEL_SEARCH_DATE_TEXT_MIN_WIDTH_PX + paddingX * 2}px`;
}

/** Date values stay on one line and are never ellipsized. */
export function getHotelSearchDateValueSx() {
  return {
    fontFamily: 'Host Grotesk',
    fontSize: `${HOTEL_SEARCH_DATE_FONT_SIZE_PX}px`,
    fontWeight: 700,
    lineHeight: '28px',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    width: 'auto',
  };
}

/**
 * Date columns never shrink below a full date. At lg they start from their
 * content and take an equal share of the leftover row, so the whitespace
 * after each value matches; below that the strip wraps instead of cropping.
 */
export function getHotelSearchDateColumnLayout() {
  return {
    flex: { xs: '1 1 100%', md: '0 0 auto', lg: '1 1 auto' },
    flexShrink: 0,
    minWidth: {
      xs: 0,
      md: getHotelSearchDateColumnMinWidth(HOTEL_SEARCH_DATE_FIELD_PADDING_X_MD_PX),
      lg: getHotelSearchDateColumnMinWidth(HOTEL_SEARCH_DATE_FIELD_PADDING_X_LG_PX),
    },
  };
}

/**
 * Below lg the destination stays content-sized. At lg it holds a fixed share
 * of the row so the other fields' equal spacing does not resize it.
 */
export function getHotelSearchDestinationLayout() {
  return {
    flex: { xs: '1 1 100%', md: '0 1 auto', lg: '0 1 30%' },
    width: { xs: '100%', md: 'auto', lg: 'auto' },
    minWidth: { xs: 0, md: HOTEL_SEARCH_DESTINATION_MIN_WIDTH, lg: '180px' },
    maxWidth: { xs: '100%', md: HOTEL_SEARCH_DESTINATION_WIDTH, lg: 'none' },
  };
}

/**
 * When the bar cannot keep every field on one line, the first three stay on
 * top and Rooms & Guests, Nationality, and Let's Go share the row below.
 * At lg both bands disappear so every field joins the wide row.
 */
export function getHotelSearchStripBandLayout(band, grouped = true) {
  if (!grouped) return { display: 'contents' };
  return {
    display: { xs: 'contents', md: 'flex', lg: 'contents' },
    width: { md: '100%' },
    alignItems: { md: 'center' },
    justifyContent: { md: 'space-between' },
    gap: { md: HOTEL_SEARCH_STRIP_COLUMN_GAP },
    marginTop: band === 'secondary' ? { md: HOTEL_SEARCH_STRIP_ROW_GAP } : undefined,
  };
}

/** Nationality stays in the lower row with the button. At lg it joins the wide row. */
export function getHotelSearchWrappedTailLayout() {
  return { display: 'contents' };
}

/**
 * Rooms & Guests and nationality. At lg they grow from their content like the
 * date columns; below lg they keep their text.
 */
export function getHotelSearchPackedFieldLayout(lgFlex = '1 1 auto', lgMinWidth = '120px') {
  return {
    flex: { xs: '1 1 100%', md: '0 0 auto', lg: lgFlex },
    width: { xs: '100%', md: 'auto' },
    minWidth: { xs: 0, md: 'auto', lg: lgMinWidth },
    maxWidth: { lg: 'none' },
  };
}
